// Classic RPG dialogue box rendered as a DOM overlay (not on canvas).
// Typewriter effect: ~40ms per character. E or Space advances. ESC skips entirely.
// On open: pauses controller. On close: resumes and fires onComplete.
//
// A line can be a plain `{ speaker, text }` OR a routing line with choices:
//   { speaker, text, choices: [{ label, next: [...lines] }, ...] }
// When a choices line completes typing, the indicator hides and choice
// buttons appear. Tapping a choice replaces the remaining queue with that
// choice's `next` array.

const TYPE_SPEED_MS = 32;
const VISITED_KEY = 'ian_world_visited';

function makeCloseBtn(onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Close');
  btn.textContent = '×';
  btn.style.cssText = `
    position: absolute;
    top: 6px;
    right: 10px;
    background: transparent;
    border: none;
    color: rgba(244, 234, 213, 0.5);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    padding: 2px 6px;
    margin: 0;
    font-family: inherit;
    transition: color 140ms ease;
    z-index: 2;
  `;
  btn.addEventListener('mouseenter', () => { btn.style.color = '#f4ead5'; });
  btn.addEventListener('mouseleave', () => { btn.style.color = 'rgba(244, 234, 213, 0.5)'; });
  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return btn;
}

export class DialogueSystem {
  constructor() {
    this.root = null;
    this.speakerEl = null;
    this.textEl = null;
    this.indicatorEl = null;
    this.choicesEl = null;
    this.lines = [];
    this.lineIndex = 0;
    this.charIndex = 0;
    this.typingTimer = null;
    this.isOpen = false;
    this.onComplete = null;
    this._keyHandler = null;
    this._mount();
  }

  _mount() {
    if (this.root) return;
    // Reuse an existing dialogue root from a previous scene instance (HMR / room change)
    const existing = document.querySelector('.iw-dialogue');
    if (existing) {
      this.root = existing;
      this.speakerEl = existing.querySelector('.iw-dialogue-speaker');
      this.textEl = existing.querySelector('.iw-dialogue-text');
      this.indicatorEl = existing.querySelector('.iw-dialogue-indicator');
      this.indicatorLabelEl = existing.querySelector('.iw-dialogue-indicator-label');
      this.indicatorChevronEl = existing.querySelector('.iw-dialogue-indicator-chevron');
      this.root.style.display = 'none';
      return;
    }
    const root = document.createElement('div');
    root.className = 'iw-dialogue';
    root.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      width: min(720px, calc(100vw - 32px));
      background: rgba(15, 12, 10, 0.94);
      color: #f4ead5;
      border: 1px solid rgba(244, 234, 213, 0.25);
      border-radius: 6px;
      padding: 18px 22px 22px;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
      z-index: 50;
      display: none;
      pointer-events: auto;
    `;

    const speaker = document.createElement('div');
    speaker.className = 'iw-dialogue-speaker';
    speaker.style.cssText = `
      font-size: 11px;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: #f6b93b;
      margin-bottom: 8px;
      opacity: 0.85;
    `;

    const text = document.createElement('div');
    text.className = 'iw-dialogue-text';
    text.style.cssText = `
      font-size: 15px;
      line-height: 1.5;
      min-height: 3em;
      white-space: pre-wrap;
    `;

    const indicator = document.createElement('div');
    indicator.className = 'iw-dialogue-indicator';
    indicator.style.cssText = `
      position: absolute;
      right: 14px;
      bottom: 8px;
      font-size: 11px;
      letter-spacing: 0.08em;
      color: #f4ead5;
      display: flex;
      align-items: center;
      gap: 6px;
    `;

    const indicatorKey = document.createElement('span');
    indicatorKey.className = 'iw-dialogue-indicator-key';
    indicatorKey.textContent = 'E';
    indicatorKey.style.cssText = `
      display: inline-block;
      min-width: 14px;
      padding: 1px 4px;
      border: 1px solid rgba(244, 234, 213, 0.55);
      border-radius: 3px;
      font-size: 9px;
      letter-spacing: 0;
      text-align: center;
      color: #f4ead5;
    `;

    const indicatorLabel = document.createElement('span');
    indicatorLabel.className = 'iw-dialogue-indicator-label';
    indicatorLabel.textContent = 'continue';
    indicatorLabel.style.cssText = `color: rgba(244, 234, 213, 0.7);`;

    const indicatorChevron = document.createElement('span');
    indicatorChevron.className = 'iw-dialogue-indicator-chevron';
    indicatorChevron.textContent = '▾';
    indicatorChevron.style.cssText = `
      color: #f4ead5;
      animation: iwBlink 1.1s ease-in-out infinite;
    `;

    indicator.appendChild(indicatorKey);
    indicator.appendChild(indicatorLabel);
    indicator.appendChild(indicatorChevron);

    if (!document.getElementById('iw-dialogue-style')) {
      const style = document.createElement('style');
      style.id = 'iw-dialogue-style';
      style.textContent = `
        @keyframes iwBlink { 0%, 100% { opacity: 0.25 } 50% { opacity: 0.95 } }
      `;
      document.head.appendChild(style);
    }

    const choices = document.createElement('div');
    choices.className = 'iw-dialogue-choices';
    choices.style.cssText = `
      display: none;
      flex-direction: column;
      gap: 8px;
      margin-top: 14px;
    `;

    root.appendChild(speaker);
    root.appendChild(text);
    root.appendChild(choices);
    root.appendChild(indicator);
    root.appendChild(makeCloseBtn(() => this.close()));
    document.body.appendChild(root);

    this.root = root;
    this.speakerEl = speaker;
    this.textEl = text;
    this.choicesEl = choices;
    this.indicatorEl = indicator;
    this.indicatorLabelEl = indicatorLabel;
    this.indicatorChevronEl = indicatorChevron;
  }

  _renderChoices(choices) {
    if (!this.choicesEl) return;
    this.choicesEl.innerHTML = '';
    for (const choice of choices) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = choice.label;
      btn.style.cssText = `
        text-align: left;
        background: rgba(244, 234, 213, 0.06);
        border: 1px solid rgba(244, 234, 213, 0.25);
        border-radius: 4px;
        padding: 10px 14px;
        color: #f4ead5;
        font-family: ui-monospace, "SF Mono", Menlo, monospace;
        font-size: 13px;
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease;
      `;
      btn.addEventListener('mouseenter', () => {
        btn.style.background = 'rgba(246, 185, 59, 0.12)';
        btn.style.borderColor = 'rgba(246, 185, 59, 0.5)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = 'rgba(244, 234, 213, 0.06)';
        btn.style.borderColor = 'rgba(244, 234, 213, 0.25)';
      });
      btn.addEventListener('click', () => this._selectChoice(choice));
      this.choicesEl.appendChild(btn);
    }
    this.choicesEl.style.display = 'flex';
    if (this.indicatorEl) this.indicatorEl.style.display = 'none';
  }

  _selectChoice(choice) {
    this.choicesEl.style.display = 'none';
    if (this.indicatorEl) this.indicatorEl.style.display = 'flex';
    // Fire the callback BEFORE branching so callers can react to the pick
    // (analytics, set flags for post-dialogue actions, etc.).
    this.onChoice?.(choice);
    if (Array.isArray(choice.next) && choice.next.length > 0) {
      // Replace the remaining queue with the chosen branch
      this.lines = choice.next;
      this.lineIndex = 0;
      this._renderLine();
    } else {
      this.close();
    }
  }

  _setIndicatorState(state) {
    // 'typing' (E to skip, dim chevron), 'continue', 'close'
    if (!this.indicatorLabelEl || !this.indicatorChevronEl) return;
    if (state === 'typing') {
      this.indicatorLabelEl.textContent = 'skip';
      this.indicatorChevronEl.style.opacity = '0.25';
      this.indicatorChevronEl.style.animationPlayState = 'paused';
    } else if (state === 'close') {
      this.indicatorLabelEl.textContent = 'close';
      this.indicatorChevronEl.style.opacity = '';
      this.indicatorChevronEl.style.animationPlayState = 'running';
    } else {
      this.indicatorLabelEl.textContent = 'continue';
      this.indicatorChevronEl.style.opacity = '';
      this.indicatorChevronEl.style.animationPlayState = 'running';
    }
  }

  open(lines, { onComplete, onChoice } = {}) {
    if (!Array.isArray(lines) || lines.length === 0) {
      onComplete?.();
      return;
    }
    this.lines = lines;
    this.lineIndex = 0;
    this.onComplete = onComplete ?? null;
    // onChoice fires with the picked choice object whenever the visitor
    // selects one — used by callers for analytics + post-choice side effects.
    this.onChoice = onChoice ?? null;
    this.isOpen = true;
    this.root.style.display = 'block';
    this._bindKeys();
    this._renderLine();
  }

  _renderLine() {
    const line = this.lines[this.lineIndex];
    this.speakerEl.textContent = line.speaker ?? '';
    this.textEl.textContent = '';
    if (this.choicesEl) this.choicesEl.style.display = 'none';
    if (this.indicatorEl) this.indicatorEl.style.display = 'flex';
    this._setIndicatorState('typing');
    this.charIndex = 0;
    this._clearTypingTimer();
    this._typeNextChar(line.text ?? '');
  }

  _typeNextChar(fullText) {
    if (this.charIndex >= fullText.length) {
      const line = this.lines[this.lineIndex];
      if (Array.isArray(line?.choices) && line.choices.length > 0) {
        // Reveal choice buttons; suppress the continue indicator.
        this._renderChoices(line.choices);
      } else {
        this._setIndicatorState(this._isLastLine() ? 'close' : 'continue');
      }
      return;
    }
    this.textEl.textContent = fullText.slice(0, this.charIndex + 1);
    this.charIndex += 1;
    this.typingTimer = setTimeout(() => this._typeNextChar(fullText), TYPE_SPEED_MS);
  }

  _isLastLine() {
    return this.lineIndex >= this.lines.length - 1;
  }

  _isFullyTyped() {
    const line = this.lines[this.lineIndex];
    return this.charIndex >= (line?.text?.length ?? 0);
  }

  _advance() {
    const line = this.lines[this.lineIndex];
    // E/Space/Enter doesn't dismiss a choices line — the visitor must pick.
    if (Array.isArray(line?.choices) && line.choices.length > 0 && this._isFullyTyped()) {
      return;
    }
    if (!this._isFullyTyped()) {
      // Fast-forward: drop straight to the end of the current line
      this._clearTypingTimer();
      this.textEl.textContent = line.text ?? '';
      this.charIndex = line.text?.length ?? 0;
      if (Array.isArray(line.choices) && line.choices.length > 0) {
        this._renderChoices(line.choices);
      } else {
        this._setIndicatorState(this._isLastLine() ? 'close' : 'continue');
      }
      return;
    }
    if (this.lineIndex < this.lines.length - 1) {
      this.lineIndex += 1;
      this._renderLine();
    } else {
      this.close();
    }
  }

  _clearTypingTimer() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  _bindKeys() {
    if (this._keyHandler) return;
    this._keyHandler = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'e' || event.key === 'E' || event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        this._advance();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  _unbindKeys() {
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  close() {
    if (!this.isOpen) return;
    this._clearTypingTimer();
    this._unbindKeys();
    this.isOpen = false;
    this.root.style.display = 'none';
    try {
      window.localStorage.setItem(VISITED_KEY, '1');
    } catch (_) {}
    const cb = this.onComplete;
    this.onComplete = null;
    cb?.();
  }
}

export function hasVisited() {
  try {
    return window.localStorage.getItem(VISITED_KEY) === '1';
  } catch (_) {
    return false;
  }
}
